/**
 * AI 项目分析工具
 * 支持多项目类型检测、智能文件读取、AI 分析
 *
 * 职责：
 * 1. 遍历项目目录，识别项目类型（Maven/Gradle/Node/Python/Go 等）
 * 2. 根据项目类型智能读取关键配置文件
 * 3. 构建结构化的 AI 分析 prompt
 * 4. 调用 AI 并解析返回结果
 */

// ============================================================
// 1. 项目类型检测规则
// ============================================================
const PROJECT_TYPES = {
  MAVEN: {
    name: 'Maven',
    label: 'Java Maven 项目',
    icon: 'el-icon-s-cooperation',
    detect: (files) => files.some(f => f.name === 'pom.xml'),
    weight: 10,
  },
  GRADLE: {
    name: 'Gradle',
    label: 'Java Gradle 项目',
    icon: 'el-icon-s-cooperation',
    detect: (files) =>
      files.some(f => f.name === 'build.gradle' || f.name === 'build.gradle.kts' || f.name === 'settings.gradle'),
    weight: 9,
  },
  NODE: {
    name: 'Node',
    label: 'Node.js 项目',
    icon: 'el-icon-s-platform',
    detect: (files) => files.some(f => f.name === 'package.json'),
    weight: 9,
  },
  PYTHON: {
    name: 'Python',
    label: 'Python 项目',
    icon: 'el-icon-s-data',
    detect: (files) =>
      files.some(f => f.name === 'requirements.txt' || f.name === 'setup.py' || f.name === 'pyproject.toml'),
    weight: 9,
  },
  GO: {
    name: 'Go',
    label: 'Go 项目',
    icon: 'el-icon-s-promotion',
    detect: (files) => files.some(f => f.name === 'go.mod'),
    weight: 9,
  },
  RUST: {
    name: 'Rust',
    label: 'Rust 项目',
    icon: 'el-icon-s-promotion',
    detect: (files) => files.some(f => f.name === 'Cargo.toml'),
    weight: 9,
  },
  DOTNET: {
    name: 'DotNet',
    label: '.NET 项目',
    icon: 'el-icon-s-platform',
    detect: (files) =>
      files.some(f => f.name.endsWith('.csproj') || f.name.endsWith('.sln') || f.name === 'appsettings.json'),
    weight: 9,
  },
  GENERIC: {
    name: 'Generic',
    label: '通用项目',
    icon: 'el-icon-folder-opened',
    detect: () => true,
    weight: 0,
  },
};

// ============================================================
// 2. 各语言的关键配置文件（优先级排列）
// ============================================================
const CONFIG_FILES_BY_TYPE = {
  MAVEN: [
    'pom.xml',
    'src/main/resources/application.yml',
    'src/main/resources/application.yaml',
    'src/main/resources/application.properties',
    'src/main/resources/application-dev.yml',
    'src/main/resources/application-prod.yml',
    'src/main/resources/bootstrap.yml',
    'src/main/resources/bootstrap.yaml',
    'src/main/resources/logback-spring.xml',
    'src/main/resources/logback.xml',
    '.mvn/jvm.config',
    'mvnw',
    'docker-compose.yml',
    'Dockerfile',
    'README.md',
  ],
  GRADLE: [
    'settings.gradle',
    'settings.gradle.kts',
    'build.gradle',
    'build.gradle.kts',
    'gradle.properties',
    'gradle/wrapper/gradle-wrapper.properties',
    'src/main/resources/application.yml',
    'src/main/resources/application.yaml',
    'src/main/resources/application.properties',
    'docker-compose.yml',
    'Dockerfile',
    'README.md',
  ],
  NODE: [
    'package.json',
    'tsconfig.json',
    'tsconfig.node.json',
    'vite.config.js',
    'vite.config.ts',
    'next.config.js',
    'next.config.mjs',
    'nuxt.config.js',
    'vue.config.js',
    'webpack.config.js',
    'webpack.config.ts',
    'babel.config.js',
    '.eslintrc.js',
    '.eslintrc.json',
    'jest.config.js',
    'jest.config.ts',
    'tailwind.config.js',
    'docker-compose.yml',
    'Dockerfile',
    'README.md',
    '.env',
    '.env.development',
    '.env.production',
  ],
  PYTHON: [
    'requirements.txt',
    'setup.py',
    'setup.cfg',
    'pyproject.toml',
    'Pipfile',
    'tox.ini',
    'manage.py',
    'app.py',
    'wsgi.py',
    'asgi.py',
    'config.py',
    'django.yml',
    'docker-compose.yml',
    'Dockerfile',
    'README.md',
    '.env',
  ],
  GO: [
    'go.mod',
    'go.sum',
    'main.go',
    'Makefile',
    'Dockerfile',
    'docker-compose.yml',
    'README.md',
    '.env',
  ],
  RUST: [
    'Cargo.toml',
    'Cargo.lock',
    'rust-toolchain.toml',
    'rust-toolchain',
    'Makefile',
    'Dockerfile',
    'README.md',
  ],
  DOTNET: [
    '*.csproj', // wildcard — need special handling
    '*.sln',
    'appsettings.json',
    'appsettings.Development.json',
    'Program.cs',
    'Startup.cs',
    'Dockerfile',
    'README.md',
  ],
  GENERIC: [
    'README.md',
    'Dockerfile',
    'docker-compose.yml',
    '.env',
    'Makefile',
  ],
};

// ============================================================
// 3. 各语言的关键源文件扩展名
// ============================================================
const SOURCE_EXTENSIONS = {
  MAVEN: ['.java', '.xml', '.yml', '.yaml', '.properties', '.sql'],
  GRADLE: ['.java', '.kt', '.kts', '.groovy', '.xml', '.yml', '.properties'],
  NODE: ['.js', '.jsx', '.ts', '.tsx', '.vue', '.json', '.css', '.scss', '.less'],
  PYTHON: ['.py', '.pyw', '.cfg', '.ini', '.yml', '.yaml'],
  GO: ['.go', '.mod', '.sum'],
  RUST: ['.rs', '.toml'],
  DOTNET: ['.cs', '.vb', '.csproj', '.sln', '.json', '.xml'],
  GENERIC: ['.json', '.yml', '.yaml', '.xml', '.config', '.env'],
};

// ============================================================
// 4. 常见框架/库检测关键词
// ============================================================
const FRAMEWORK_PATTERNS = {
  'Spring Boot': { types: ['MAVEN', 'GRADLE'], keywords: ['spring-boot-starter', 'spring-boot'] },
  'Spring Cloud': { types: ['MAVEN', 'GRADLE'], keywords: ['spring-cloud-starter', 'spring-cloud'] },
  'MyBatis': { types: ['MAVEN', 'GRADLE'], keywords: ['mybatis-spring-boot', 'mybatis-plus'] },
  'Vue.js': { types: ['NODE'], keywords: ['vue', 'vue-router', 'pinia', 'vuex'] },
  'React': { types: ['NODE'], keywords: ['react', 'react-dom', 'next'] },
  'Nuxt.js': { types: ['NODE'], keywords: ['nuxt'] },
  'Next.js': { types: ['NODE'], keywords: ['next'] },
  'Express': { types: ['NODE'], keywords: ['express'] },
  'Flask': { types: ['PYTHON'], keywords: ['flask'] },
  'Django': { types: ['PYTHON'], keywords: ['django'] },
  'FastAPI': { types: ['PYTHON'], keywords: ['fastapi'] },
  'Gin': { types: ['GO'], keywords: ['gin'] },
  'Gorm': { types: ['GO'], keywords: ['gorm'] },
  'Axum': { types: ['RUST'], keywords: ['axum'] },
  'Actix': { types: ['RUST'], keywords: ['actix'] },
};

// 跳过分析的目录
const SKIP_DIRS = new Set([
  'node_modules', 'target', '.git', '.idea', 'dist', 'build', '.mvn',
  '.gradle', '__pycache__', '.next', '.nuxt', '.cache', 'coverage',
  '.husky', '.github', 'assets', 'static', 'public',
]);

// 跳过分析的文件扩展名（二进制/媒体文件）
const SKIP_EXTENSIONS = new Set([
  '.class', '.jar', '.war', '.ear', '.png', '.jpg', '.jpeg', '.gif',
  '.svg', '.ico', '.woff', '.woff2', '.eot', '.ttf', '.otf',
  '.mp4', '.mp3', '.avi', '.mov', '.pdf', '.doc', '.docx',
  '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.tar', '.gz',
  '.7z', '.rar', '.exe', '.dll', '.so', '.dylib', '.o', '.a',
  '.lib', '.obj', '.pyc', '.pyo', '.DS_Store',
]);

// ============================================================
// 5. 核心逻辑
// ============================================================

/**
 * 遍历目录并返回文件列表
 * @param {FileSystemDirectoryHandle} dirHandle
 * @param {string} basePath
 * @param {boolean} includeDirNames — 是否包含目录名在结果中（用于结构展示）
 * @returns {Promise<{files: Array, structureLines: Array}>}
 */
export async function walkDirectory(dirHandle, basePath = '', includeDirNames = true) {
  const files = [];
  const structureLines = [];
  await walkDirRecursive(dirHandle, basePath, files, structureLines, includeDirNames);
  return { files, structureLines };
}

async function walkDirRecursive(handle, path, files, structureLines, includeDirNames) {
  for await (const entry of handle.values()) {
    const entryPath = path ? `${path}/${entry.name}` : entry.name;

    if (entry.kind === 'directory') {
      if (SKIP_DIRS.has(entry.name)) continue;
      if (includeDirNames) {
        structureLines.push(`[DIR] ${entryPath}`);
      }
      await walkDirRecursive(entry, entryPath, files, structureLines, includeDirNames);
    } else {
      const ext = getFileExtension(entry.name);
      if (!SKIP_EXTENSIONS.has(ext)) {
        files.push({ name: entry.name, path: entryPath, handle: entry, ext });
        structureLines.push(`[FILE] ${entryPath}`);
      }
    }
  }
}

function getFileExtension(filename) {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.substring(idx).toLowerCase() : '';
}

/**
 * 检测项目类型
 * @param {Array<{name: string, path: string}>} files
 * @returns {{primary: object, secondary: Array}}
 */
export function detectProjectType(files) {
  const detected = [];

  for (const [key, type] of Object.entries(PROJECT_TYPES)) {
    if (key === 'GENERIC') continue;
    if (type.detect(files)) {
      detected.push({ ...type, key });
    }
  }

  // 按权重排序
  detected.sort((a, b) => b.weight - a.weight);

  return {
    primary: detected.length > 0 ? detected[0] : PROJECT_TYPES.GENERIC,
    all: detected,
  };
}

/**
 * 根据项目类型获取需要读取的配置文件路径列表
 */
export function getRelevantConfigFiles(projectType) {
  return CONFIG_FILES_BY_TYPE[projectType] || CONFIG_FILES_BY_TYPE.GENERIC;
}

/**
 * 读取指定路径文件的内容（从 directory handle）
 */
export async function readFileContent(dirHandle, filePath) {
  try {
    const parts = filePath.split('/');
    let handle = dirHandle;

    for (let i = 0; i < parts.length; i++) {
      if (i === parts.length - 1) {
        // 最后一个 — 应该是文件
        if (handle.kind === 'file') {
          const file = await handle.getFile();
          return await file.text();
        }
        // 如果是 wildcard 模式，需要特殊处理
        if (parts[i].startsWith('*')) {
          return null; // wildcard 需要在外部处理
        }
        try {
          const fileHandle = await handle.getFileHandle(parts[i]);
          const file = await fileHandle.getFile();
          return await file.text();
        } catch (e) {
          return null;
        }
      } else {
        handle = await handle.getDirectoryHandle(parts[i]);
      }
    }
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * 从目录 handle 中智能读取关键配置文件
 */
export async function readKeyConfigFiles(dirHandle, files, projectType) {
  const configPaths = getRelevantConfigFiles(projectType);
  const result = {};
  const fileMap = {};
  files.forEach(f => { fileMap[f.path] = f; });

  for (const configPath of configPaths) {
    // 处理通配符模式
    if (configPath.includes('*')) {
      const prefix = configPath.substring(0, configPath.indexOf('*'));
      const suffix = configPath.substring(configPath.indexOf('*') + 1);
      for (const f of files) {
        if (f.path.startsWith(prefix) && f.path.endsWith(suffix)) {
          if (!result[configPath]) result[configPath] = {};
          result[configPath][f.name] = await readFileContentFromHandle(f.handle);
        }
      }
      if (result[configPath] && Object.keys(result[configPath]).length === 0) {
        delete result[configPath];
      }
      continue;
    }

    const file = fileMap[configPath];
    if (file) {
      result[configPath] = await readFileContentFromHandle(file.handle);
    }
  }

  return result;
}

async function readFileContentFromHandle(fileHandle) {
  try {
    const file = await fileHandle.getFile();
    const text = await file.text();
    // 限制文件大小，防止超大文件
    return text.length > 30000 ? text.substring(0, 30000) + '\n... [文件过大，已截断]' : text;
  } catch (e) {
    return null;
  }
}

/**
 * 检测项目中的框架/技术栈
 */
export function detectFrameworks(configContents, projectType) {
  const frameworks = [];
  const combined = Object.values(configContents).flatMap(v => {
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return Object.values(v);
    return [];
  }).join('\n').toLowerCase();

  for (const [name, pattern] of Object.entries(FRAMEWORK_PATTERNS)) {
    if (!pattern.types.includes(projectType)) continue;
    if (pattern.keywords.some(kw => combined.includes(kw.toLowerCase()))) {
      frameworks.push(name);
    }
  }

  return frameworks;
}

/**
 * 构建结构化的目录树文本（用于 AI prompt）
 */
const SOURCE_EXT = /\.(java|vue|js|jsx|ts|tsx|py|go|rs|php|c|cpp|h|hpp|cs|kt|scala)$/i;

export function buildStructureTree(structureLines, maxLines = 60) {
  // 只保留目录行和配置文件，过滤掉源码文件
  const important = structureLines.filter(l => {
    if (l.startsWith('[DIR]')) return true;
    if (SOURCE_EXT.test(l)) return false; // 过滤源码文件
    // 配置文件保留
    return /pom\.xml|build\.gradle|package\.json|application\.|Dockerfile|docker-compose|README|\.yml|\.yaml|\.properties|\.env|\.conf|settings\.xml|\.gitignore|\.editorconfig|\.npmrc|Makefile|\.xml/i.test(l);
  });
  if (important.length <= maxLines) return important.join('\n');
  return [...important.slice(0, maxLines), `\n... 共 ${structureLines.length} 项（显示 ${maxLines} 项关键结构）`].join('\n');
}

/**
 * 根据项目类型构建 AI 分析 prompt
 */
export function buildAnalysisPrompt({
  projectName,
  projectType,
  structureTree,
  configContents,
  frameworks,
  customInstructions = '',
}) {
  const typeLabel = projectType.label || '通用项目';
  const frameworkText = frameworks.length > 0
    ? `检测到框架/库：${frameworks.join(', ')}`
    : '未检测到特定框架';

  // 构建配置文件摘要
  let configSummary = '';
  for (const [path, content] of Object.entries(configContents)) {
    if (content) {
      const preview = typeof content === 'string'
        ? content.substring(0, 2000)
        : JSON.stringify(content, null, 2).substring(0, 2000);
      configSummary += `\n===== ${path} =====\n${preview}\n`;
    }
  }

  // 根据不同项目类型生成不同的分析指令
  const typeSpecificInstructions = getTypeSpecificInstructions(projectType.key);

  return `你是一个专业的多语言项目架构分析师。请分析以下项目并提取结构化信息。

## 项目信息
- 项目名称：${projectName}
- 项目类型：${typeLabel}
- ${frameworkText}

## 项目目录结构
${structureTree}

## 关键配置文件
${configSummary.substring(0, 15000)}

${customInstructions}

## 请从以上信息中提取以下内容，以 **纯 JSON 格式** 返回（不要 markdown 代码块标记，不要 \`\`\`json）：

{
  "projectDescription": "项目简短描述（1-2句话，说明项目的业务定位和主要功能）",
  "projectType": "${projectType.key}",
  "frameworks": [检测到的框架/库列表],
  "modules": ${typeSpecificInstructions.modulesPrompt},
  "configSchema": ${typeSpecificInstructions.configSchemaPrompt},
  "envRequirements": ${typeSpecificInstructions.envRequirementsPrompt}
}`;
}

function getTypeSpecificInstructions(projectType) {
  const common = `环境要求数组，每个元素有 name/version/type 属性，type 可选值: runtime, build, tool`;

  switch (projectType) {
    case 'MAVEN':
    case 'GRADLE':
      return {
        modulesPrompt: `从目录结构和配置文件中分析出的模块列表。每个模块有 name/type/description/techStack 属性。type 可选值: api, web, service, common, gateway, job。对于 Maven 项目，优先从 pom.xml 的 <modules> 标签提取；对于 Gradle 项目，从 settings.gradle 的 include 语句提取。
示例: [{"name":"user-api","type":"api","description":"用户接口模块","techStack":"Spring Cloud Feign"},{"name":"user-service","type":"service","description":"用户服务模块","techStack":"Spring Boot, MyBatis"}]`,
        configSchemaPrompt: `配置清单，描述该项目运行时需要哪些外部配置（数据库、Redis、ES、OSS、消息队列等）。返回数组，按 group 分组，每个 group 包含 group/fields 数组，每个 field 有 key/label/type/required/default/description/order 属性。
type 可选值: input, password, textarea, number, select
如果字段是 select 类型，还需要 options 数组（{label, value}）
优先从 application.yml/properties 中提取配置项。
如果无法提取具体配置，返回 [{"group":"基础配置","fields":[{"key":"appPort","label":"应用端口","type":"input","required":true,"default":"8080","order":1}]}]`,
        envRequirementsPrompt: `${common}
对于 Java 项目，需要 JDK/Maven/Gradle 等环境。
从 pom.xml 的 java.version / maven.compiler.source 或 build.gradle 的 sourceCompatibility 提取版本信息。
示例: [{"name":"JDK","version":"17","type":"runtime"},{"name":"Maven","version":"3.8+","type":"build"}]`,
      };

    case 'NODE':
      return {
        modulesPrompt: `分析项目是否有 monorepo 结构（如 workspaces, lerna, nx）。如果不是 monorepo，返回单个模块。每个模块有 name/type/description 属性。
示例（单模块）: [{"name":"web-app","type":"web","description":"前端应用","techStack":"Vue 3 + Element Plus"}]
示例（多模块）: [{"name":"admin","type":"web","description":"管理后台"},{"name":"client","type":"web","description":"客户端页面"}]`,
        configSchemaPrompt: `配置清单，描述该项目运行时需要哪些环境变量或外部配置（API 地址、认证密钥等）。优先从 .env 文件或 package.json 的 scripts 部分提取。
示例: [{"group":"API配置","fields":[{"key":"apiBaseUrl","label":"API地址","type":"input","required":true,"default":"http://localhost:8080","order":1}]},{"group":"认证配置","fields":[{"key":"authSecret","label":"认证密钥","type":"password","required":true,"order":1}]}]`,
        envRequirementsPrompt: `${common}
对于 Node.js 项目，需要 Node.js 版本。
从 package.json 的 engines.node 或 .nvmrc 提取。
示例: [{"name":"Node.js","version":"18+","type":"runtime"},{"name":"npm","version":"9+","type":"build"}]`,
      };

    case 'PYTHON':
      return {
        modulesPrompt: `分析项目的模块结构。对于 Django 项目，每个 app 是一个模块。对于 Flask/FastAPI，通常为单模块。
示例: [{"name":"api","type":"api","description":"API服务","techStack":"FastAPI"}]`,
        configSchemaPrompt: `配置清单，描述 Python 项目运行所需的外部配置。
示例: [{"group":"数据库配置","fields":[{"key":"dbUrl","label":"数据库连接","type":"input","required":true,"order":1}]}]`,
        envRequirementsPrompt: `${common}
从 requirements.txt 或 pyproject.toml 提取 Python 版本和关键依赖。
示例: [{"name":"Python","version":"3.10+","type":"runtime"},{"name":"pip","version":"21+","type":"build"}]`,
      };

    case 'GO':
      return {
        modulesPrompt: `分析 Go 项目的模块结构。从 go.mod 提取模块名，分析目录结构识别子模块。
示例: [{"name":"cmd/api","type":"api","description":"API服务","techStack":"Gin"},{"name":"internal/service","type":"service","description":"业务服务层"}]`,
        configSchemaPrompt: `配置清单，描述 Go 项目运行所需的外部配置。
示例: [{"group":"服务配置","fields":[{"key":"port","label":"监听端口","type":"input","default":"8080","order":1}]}]`,
        envRequirementsPrompt: `${common}
从 go.mod 提取 Go 版本。
示例: [{"name":"Go","version":"1.21+","type":"runtime"}]`,
      };

    default:
      return {
        modulesPrompt: `从目录结构分析出的模块/组件列表。根据常见项目结构推断。每个模块有 name/type/description 属性。
如果项目结构清晰（如 api/service/common 等目录），分别列出。
否则返回简化的单模块。
示例: [{"name":"main","type":"web","description":"主应用"},{"name":"api","type":"api","description":"API接口"}]`,
        configSchemaPrompt: `配置清单，描述该项目运行所需的外部配置。
如果无法分析具体配置，返回简单的通用配置：
示例: [{"group":"服务器配置","fields":[{"key":"port","label":"端口","type":"input","default":"8080","order":1}]},{"group":"数据库配置","fields":[{"key":"dbUrl","label":"数据库连接地址","type":"input","required":true,"order":1}]}]`,
        envRequirementsPrompt: `${common}
从 Dockerfile 或 README 等文件提取环境要求。
示例: [{"name":"运行时","version":"latest","type":"runtime"}]`,
      };
  }
}

/**
 * 调用 AI API
 */
export async function callAI(prompt, $ai) {
  const endpoint = ($ai && $ai.endpoint) ? $ai.endpoint : '/data/v1/chat/completions';
  const model = ($ai && $ai.model) || 'ernie-4.0';

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: model,
      messages: [{ role: 'user', content: prompt }],
      stream: false,
    }),
  });

  if (!response.ok) {
    throw new Error(`AI API 请求失败: ${response.status} ${response.statusText}`);
  }

  const data = await response.json();

  // 兼容各种返回格式
  let content;
  if (data.choices && data.choices[0]) {
    content = data.choices[0].message
      ? data.choices[0].message.content
      : data.choices[0].content;
  } else if (data.result) {
    content = data.result;
  } else if (data.data) {
    content = typeof data.data === 'string' ? data.data : JSON.stringify(data.data);
  } else {
    content = JSON.stringify(data);
  }

  // 已经是对象 → 直接返回（兼容某些 AI 返回格式）
  if (typeof content === 'object' && content !== null) {
    return content;
  }

  // 字符串 → 清理 markdown 代码块标记后解析
  if (typeof content === 'string') {
    content = content.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '').trim();
    return JSON.parse(content);
  }

  throw new Error(`意外的 AI 响应类型: ${typeof content}`);
}

/**
 * 校验 AI 返回结果的完整性，补充缺失字段
 */
export function normalizeAnalysisResult(result) {
  const defaults = {
    projectDescription: '',
    projectType: 'GENERIC',
    frameworks: [],
    modules: [],
    configSchema: [],
    envRequirements: [],
  };

  const normalized = { ...defaults, ...result };

  if (typeof normalized.frameworks === 'string') {
    normalized.frameworks = normalized.frameworks.split(',').map(s => s.trim()).filter(Boolean);
  }
  if (!Array.isArray(normalized.modules)) normalized.modules = [];
  if (!Array.isArray(normalized.configSchema)) normalized.configSchema = [];
  if (!Array.isArray(normalized.envRequirements)) normalized.envRequirements = [];

  // 规范化 modules
  normalized.modules = normalized.modules.map(m => ({
    name: m.name || m.code || 'unknown',
    type: m.type || 'module',
    description: m.description || '',
    techStack: m.techStack || '',
  }));

  return normalized;
}

/**
 * 构建源码分析 prompt — 用于 Step 5 的智能源码扫描
 */
export function buildSourceAnalysisPrompt({
  projectType,
  files,
  projectDescription = '',
  customInstructions = '',
}) {
  const fileDescriptions = files
    .map(f => `- ${f.filePath}${f.content ? '\n  内容预览: ' + f.content.substring(0, 500) : ''}`)
    .join('\n');

  const typeLabel = PROJECT_TYPES[projectType] ? PROJECT_TYPES[projectType].label : '通用项目';

  return `你是一个专业的源码分析师。请分析以下 ${typeLabel} 项目的源码文件，对每个文件进行分类。

## 项目描述
${projectDescription || '未提供'}

## 待分类文件
${fileDescriptions}

${customInstructions}

## 返回格式（纯 JSON 数组，不要 markdown 代码块）
[
  {
    "fileName": "文件名（用返回时的 key 匹配）",
    "type": "分类类型",
    "code": "业务编码（如 UserController → user，OrderService → order，从文件名推断）",
    "description": "文件功能描述（一句话）"
  }
]

## 分类类型说明
- Java 文件: ENUM / ENTITY / CONTROLLER / SERVICE / SERVICE_IMPL / MAPPER / CONFIG / DTO / VO / UTIL / OTHER
- Python 文件: MODEL / VIEW / CONTROLLER / SERVICE / CONFIG / FORM / UTIL / ADMIN / OTHER
- Node 文件: COMPONENT / PAGE / STORE / API / CONFIG / UTIL / LAYOUT / ROUTER / OTHER
- 通用: API / CONFIG / MODEL / SERVICE / UTIL / OTHER

请根据文件内容准确判断。如果不确定，使用 OTHER。`;
}

/**
 * 验证 AI 返回的源码分析结果
 */
export function normalizeSourceAnalysisResult(result) {
  if (!Array.isArray(result)) return [];
  return result.map(r => ({
    fileName: r.fileName || '',
    type: r.type || 'OTHER',
    code: r.code || '',
    description: r.description || '',
  }));
}

/**
 * ============================================================
 * 7. 多轮对话式 AI 分析（自主探索模式）
 * ============================================================
 */

/**
 * 根据项目类型生成默认的配置清单和环境要求（AI 分析失败时的保底方案）
 */
function getDefaultAnalysis(projectTypeKey, projectName) {
  const defaults = {
    projectDescription: `${projectName || '未命名项目'} - 基于 ${projectTypeKey || 'GENERIC'} 模板`,
    configSchema: [],
    envRequirements: [],
  };

  switch (projectTypeKey) {
    case 'MAVEN':
    case 'GRADLE':
      defaults.configSchema.push({
        group: '服务器配置',
        fields: [
          { key: 'serverPort', label: '应用端口', type: 'input', required: true, default: '8080', order: 1, description: '应用服务监听端口' },
        ],
      });
      defaults.configSchema.push({
        group: '数据库配置',
        fields: [
          { key: 'dbUrl', label: '数据库连接地址', type: 'input', required: true, default: 'jdbc:mysql://localhost:3306/db', order: 1, description: 'JDBC 数据库连接' },
          { key: 'dbUsername', label: '数据库用户名', type: 'input', required: true, default: 'root', order: 2 },
          { key: 'dbPassword', label: '数据库密码', type: 'password', required: true, default: '', order: 3 },
        ],
      });
      defaults.configSchema.push({
        group: 'Redis 配置',
        fields: [
          { key: 'redisHost', label: 'Redis 地址', type: 'input', required: false, default: 'localhost', order: 1 },
          { key: 'redisPort', label: 'Redis 端口', type: 'input', required: false, default: '6379', order: 2 },
        ],
      });
      defaults.envRequirements.push(
        { name: 'JDK', version: '17', type: 'runtime' },
        { name: projectTypeKey === 'MAVEN' ? 'Maven' : 'Gradle', version: projectTypeKey === 'MAVEN' ? '3.8+' : '8+', type: 'build' },
      );
      break;
    case 'NODE':
      defaults.configSchema.push({
        group: '应用配置',
        fields: [
          { key: 'port', label: '服务端口', type: 'input', required: true, default: '3000', order: 1 },
          { key: 'apiBaseUrl', label: 'API 基础地址', type: 'input', required: true, default: 'http://localhost:8080', order: 2 },
        ],
      });
      defaults.envRequirements.push(
        { name: 'Node.js', version: '18+', type: 'runtime' },
        { name: 'npm', version: '9+', type: 'build' },
      );
      break;
    case 'PYTHON':
      defaults.configSchema.push({
        group: '应用配置',
        fields: [
          { key: 'port', label: '服务端口', type: 'input', required: true, default: '8000', order: 1 },
          { key: 'debug', label: '调试模式', type: 'select', required: false, default: 'true', order: 2, options: [{ label: '开启', value: 'true' }, { label: '关闭', value: 'false' }] },
        ],
      });
      defaults.configSchema.push({
        group: '数据库配置',
        fields: [
          { key: 'dbUrl', label: '数据库连接', type: 'input', required: true, default: 'sqlite:///app.db', order: 1 },
        ],
      });
      defaults.envRequirements.push(
        { name: 'Python', version: '3.10+', type: 'runtime' },
        { name: 'pip', version: '21+', type: 'build' },
      );
      break;
    case 'GO':
      defaults.configSchema.push({
        group: '服务配置',
        fields: [
          { key: 'port', label: '监听端口', type: 'input', required: true, default: '8080', order: 1 },
        ],
      });
      defaults.envRequirements.push(
        { name: 'Go', version: '1.21+', type: 'runtime' },
      );
      break;
    default:
      defaults.configSchema.push({
        group: '基础配置',
        fields: [
          { key: 'port', label: '端口', type: 'input', required: false, default: '8080', order: 1 },
        ],
      });
      defaults.envRequirements.push(
        { name: '运行时', version: 'latest', type: 'runtime' },
      );
  }

  return defaults;
}

/**
 * 按路径从目录句柄中读取文件内容
 */
export async function readFileByPath(dirHandle, filePath, maxLength = 3000) {
  try {
    const parts = filePath.split('/');
    let handle = dirHandle;
    for (let i = 0; i < parts.length; i++) {
      if (i === parts.length - 1) {
        const fileHandle = await handle.getFileHandle(parts[i]);
        const file = await fileHandle.getFile();
        const text = await file.text();
        return text.length > maxLength
          ? text.substring(0, maxLength) + '\n... [文件过大，已截断]'
          : text;
      } else {
        handle = await handle.getDirectoryHandle(parts[i]);
      }
    }
    return null;
  } catch (e) {
    return null;
  }
}

/**
 * 调用 AI API（完整消息历史，返回原始文本）
 */
async function callRaw(messages, $ai) {
  const endpoint = ($ai && $ai.endpoint) || '/data/v1/chat/completions';
  const model = ($ai && $ai.model) || 'ernie-4.0';

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model,
      messages,
      stream: false,
      temperature: 0.3,
    }),
  });

  if (!response.ok) throw new Error(`AI API 请求失败: ${response.status}`);

  const data = await response.json();

  // 兼容各种返回格式
  if (data.choices && data.choices[0]) {
    const c = data.choices[0].message
      ? data.choices[0].message.content
      : data.choices[0].content;
    if (c) return c;
  }
  if (data.result) {
    const r = typeof data.result === 'string' ? data.result : JSON.stringify(data.result);
    if (r && r !== '""') return r;
  }
  if (data.data && typeof data.data === 'string' && data.data.trim()) {
    return data.data;
  }
  if (data.data && typeof data.data === 'object') {
    return JSON.stringify(data.data);
  }
  throw new Error('AI 返回内容为空');
}

/**
 * 从 AI 原始回复中解析决策 JSON
 * 宽松模式：尝试从任意文本中提取 JSON
 */
function parseAIDecision(rawText) {
  let cleaned = rawText.trim()
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```\s*$/i, '')
    .trim();

  let parsed;
  try {
    parsed = JSON.parse(cleaned);
  } catch (e) {
    // 尝试从中提取 {...}
    const match = cleaned.match(/\{[\s\S]*\}/);
    if (match) {
      try { parsed = JSON.parse(match[0]); } catch (e2) {
        throw new Error(`JSON 解析失败: ${cleaned.substring(0, 100)}`);
      }
    } else {
      throw new Error(`响应中没有 JSON: ${cleaned.substring(0, 100)}`);
    }
  }

  if (!parsed.action || !['continue', 'done'].includes(parsed.action)) {
    throw new Error(`无效 action: ${parsed.action}`);
  }

  if (parsed.action === 'continue') {
    if (!Array.isArray(parsed.filesToRead)) parsed.filesToRead = [];
    parsed.filesToRead = [...new Set(parsed.filesToRead)].slice(0, 5);
  }

  return parsed;
}

/**
 * 构建第一条用户消息
 * 格式优先：JSON 示例放在最前面
 */
function buildFirstMessage({ projectName, typeLabel, typeKey, frameworkText, structureTree }) {
  return `你必须只返回 RAW JSON，不要任何其他文字，不要 markdown 代码块。

继续探索示例：
{"action":"continue","filesToRead":["pom.xml"],"reasoning":"查看 Maven 依赖配置"}

结束分析示例：
{"action":"done","result":{"projectDescription":"Spring Boot 项目","configSchema":[{"group":"数据库","fields":[{"key":"url","label":"连接地址","type":"input","required":true,"default":"jdbc:mysql://localhost:3306/db","order":1}]}],"envRequirements":[{"name":"JDK","version":"17","type":"runtime"}]}}

---

分析流程：查看文件树 → 请求读文件 → 收到文件内容 → 继续或结束

项目：${projectName || '未命名'}
类型：${typeLabel}
框架：${frameworkText}

文件树：
${structureTree}

只返回 JSON，不要任何其他文字。`;
}

/**
 * 多轮对话式 AI 项目分析
 *
 * 每轮把累积上下文作为单条 user message 发送（兼容不支持多消息历史的 API）。
 * AI 自主决定读哪些文件，逐轮探索直到收集足够信息。
 *
 * @param {FileSystemDirectoryHandle} dirHandle
 * @param {object} scanResult - { structureLines, projectFiles, detectedProjectType, detectedFrameworks, scaffoldName }
 * @param {object} $ai - AI 配置
 * @param {function} onProgress - 进度回调({ phase, message, filesToRead? })
 * @returns {Promise<object>} normalizeAnalysisResult 兼容结果
 */
export async function runMultiTurnAnalysis(dirHandle, scanResult, $ai, onProgress = () => {}) {
  const MAX_ROUNDS = 6;

  const {
    structureLines = [],
    projectFiles = [],
    detectedProjectType = null,
    detectedFrameworks = [],
    scaffoldName = '',
  } = scanResult;

  const structureTree = buildStructureTree(structureLines);
  const typeKey = (detectedProjectType && detectedProjectType.key) || 'GENERIC';
  const typeLabel = (detectedProjectType && detectedProjectType.label) || '通用项目';
  const frameworkText = detectedFrameworks.length > 0
    ? detectedFrameworks.join(', ')
    : '未检测到';

  const defaultResult = getDefaultAnalysis(typeKey, scaffoldName);
  const requestedFiles = new Set();

  // 累积上下文（单条 user message，每轮追加新内容）
  let context = buildFirstMessage({
    projectName: scaffoldName || '未命名',
    typeLabel,
    typeKey,
    frameworkText,
    structureTree,
  });

  // ---- 多轮对话循环（用 callAI 发送单条消息） ----
  for (let round = 0; round < MAX_ROUNDS; round++) {
    onProgress({ phase: 'thinking', message: `AI 分析中（第 ${round + 1} 轮）...` });

    // 调用 AI（使用已验证的 callAI，单条 user message）
    let result;
    try {
      result = await callAI(context, $ai);
    } catch (e) {
      console.error(`第 ${round + 1} 轮 AI 调用失败:`, e);
      onProgress({ phase: 'error', message: `AI 调用失败: ${e.message}` });
      break;
    }

    // 结束分析
    if (result.action === 'done') {
      onProgress({ phase: 'done', message: `分析完成（共 ${round + 1} 轮）` });

      const finalResult = {
        ...defaultResult,
        ...result.result,
        configSchema: result.result?.configSchema?.length > 0
          ? result.result.configSchema
          : defaultResult.configSchema,
        envRequirements: result.result?.envRequirements?.length > 0
          ? result.result.envRequirements
          : defaultResult.envRequirements,
      };
      return normalizeAnalysisResult(finalResult);
    }

    // 继续探索：读取文件
    if (result.action !== 'continue') break;

    const filesToRead = (result.filesToRead || []).filter(f => !requestedFiles.has(f));
    if (filesToRead.length === 0) break;

    filesToRead.forEach(f => requestedFiles.add(f));

    onProgress({
      phase: 'reading',
      message: `读取: ${filesToRead.join(', ')}`,
      filesToRead,
    });

    // 读取文件
    const contents = await Promise.all(
      filesToRead.map(async (filePath) => ({
        filePath,
        content: await readFileByPath(dirHandle, filePath, 3000),
      }))
    );

    // 追加到上下文
    let append = '\n\n';
    for (const { filePath, content } of contents) {
      append += `--- ${filePath} ---\n${content !== null ? content : '[文件不存在]'}\n\n`;
    }
    append += `只返回 JSON：\n`;
    append += `{"action":"continue","filesToRead":["其他需要看的文件"],"reasoning":"原因"}\n`;
    append += `或\n`;
    append += `{"action":"done","result":{"projectDescription":"","configSchema":[],"envRequirements":[]}}`;

    context += append;
  }

  // 超出轮数 → 保底
  onProgress({ phase: 'done', message: '使用默认模板生成配置' });
  return normalizeAnalysisResult({
    projectDescription: defaultResult.projectDescription,
    projectType: typeKey,
    frameworks: detectedFrameworks,
    configSchema: defaultResult.configSchema,
    envRequirements: defaultResult.envRequirements,
  });
}

export default {
  walkDirectory,
  detectProjectType,
  getRelevantConfigFiles,
  readKeyConfigFiles,
  detectFrameworks,
  buildStructureTree,
  buildAnalysisPrompt,
  callAI,
  normalizeAnalysisResult,
  buildSourceAnalysisPrompt,
  normalizeSourceAnalysisResult,
  PROJECT_TYPES,
  readFileByPath,
  runMultiTurnAnalysis,
};
